import { ALCHEMY } from '../data/alchemy.js';
import { BIOME_IDS } from '../data/biomes.js';
import { BOSS_MOB_IDS, BOSSES } from '../data/bosses.js';
import { RARITIES } from '../data/items.js';
import { biomeOfMob, BOSS_TROPHIES, LOOT_POOLS } from '../data/loot.js';
import { MERCHANT_SUPPLIES } from '../data/merchant.js';
import { MOBS } from '../data/mobs.js';
import { UPGRADE_MATERIALS } from '../data/upgrade-materials.js';
function buildIndex() {
    const index = new Map();
    const add = (templateId, source) => {
        const sources = index.get(templateId) ?? [];
        sources.push(source);
        index.set(templateId, sources);
    };
    // Ordinary creatures first, by chance: the ones worth hunting on top.
    const drops = Object.values(MOBS)
        .filter((mob) => !mob.peaceful && mob.rarity === 'common' && mob.rank === 'ordinary')
        .flatMap((mob) => mob.loot.map((entry) => ({
        templateId: entry.templateId,
        mobId: mob.id,
        chance: entry.chance,
    })))
        .sort((a, b) => b.chance - a.chance);
    for (const drop of drops) {
        add(drop.templateId, { kind: 'mob', mobId: drop.mobId, chance: drop.chance });
    }
    for (const biome of BIOME_IDS) {
        const materials = UPGRADE_MATERIALS[biome];
        add(materials.common.id, { kind: 'chest', biome, campsOnly: false });
        add(materials.rare.id, { kind: 'guardian', biome });
        add(materials.rare.id, { kind: 'elite', biome });
        add(materials.rare.id, { kind: 'chest', biome, campsOnly: true });
        add(materials.epic.id, { kind: 'elite', biome });
    }
    for (const bossId of BOSS_MOB_IDS) {
        const materials = UPGRADE_MATERIALS[BOSSES[bossId].biome];
        add(materials.epic.id, { kind: 'boss', mobId: bossId });
        add(materials.rare.id, { kind: 'boss', mobId: bossId });
        const trophy = BOSS_TROPHIES[BOSSES[bossId].biome];
        if (trophy !== undefined) {
            add(trophy.templateId, { kind: 'boss', mobId: bossId, chance: trophy.chance });
        }
    }
    for (const biome of BIOME_IDS) {
        for (const rarity of RARITIES) {
            for (const id of LOOT_POOLS[biome][rarity])
                add(id, { kind: 'biome-loot', biome });
        }
        for (const id of LOOT_POOLS[biome].common)
            add(id, { kind: 'merchant' });
    }
    for (const id of MERCHANT_SUPPLIES)
        add(id, { kind: 'merchant' });
    for (const biome of Object.keys(ALCHEMY)) {
        add(ALCHEMY[biome].light.id, { kind: 'alchemist', biome });
        add(ALCHEMY[biome].strong.id, { kind: 'alchemist', biome });
    }
    // A piece in two bands' pools, or sold in every village, says it once.
    for (const [id, sources] of index) {
        const seen = new Set();
        index.set(id, sources.filter((source) => {
            const key = JSON.stringify(source.kind === 'mob' ? { m: source.mobId } : source);
            if (seen.has(key))
                return false;
            seen.add(key);
            return true;
        }));
    }
    return index;
}
const SOURCES = buildIndex();
/** Every way to come by this item; empty for what only a shop or a feat gives. */
export function itemSources(templateId) {
    return SOURCES.get(templateId) ?? [];
}
/** The biome a creature source hunts in, for the tooltip to say where to go. */
export function sourceBiome(source) {
    if (source.kind === 'merchant')
        return undefined;
    if (source.kind === 'mob' || source.kind === 'boss')
        return biomeOfMob(source.mobId);
    return source.biome;
}
//# sourceMappingURL=item-sources.js.map